import { t } from "i18next";
import { uiDateLocale } from "@/lib/date-locale";
import { expiresAt } from "@/lib/offline-recording/limits";

/** "38 min", "1 h 05 min", "45 s". */
export const formatRecordingDuration = (ms: number): string => {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60)
    return t("offlineRecording.duration.seconds", "{{count}} s", {
      count: seconds,
    });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60)
    return t("offlineRecording.duration.minutes", "{{count}} min", {
      count: minutes,
    });
  const hours = Math.floor(minutes / 60);
  return t("offlineRecording.duration.hours", "{{hours}} h {{minutes}} min", {
    hours,
    minutes: String(minutes % 60).padStart(2, "0"),
  });
};

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** "10:42" today, "Mon 19 Oct, 10:42" otherwise. */
export const formatRecordingStart = (
  createdAt: number,
  now = new Date(),
): string => {
  const date = new Date(createdAt);
  const time = date.toLocaleTimeString(uiDateLocale(), {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (sameDay(date, now)) return time;
  const day = date.toLocaleDateString(uiDateLocale(), {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  return `${day}, ${time}`;
};

export const formatExpiry = (createdAt: number): string =>
  new Date(expiresAt(createdAt)).toLocaleDateString(uiDateLocale(), {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

export const formatSize = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
